import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/ServicePage.css';
import { setPageMeta } from '../utils/seo';

const StartupConsultancy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    setPageMeta(
      'Startup Growth Consultancy | HRCONSOL',
      'End-to-end startup consulting: strategy, team building, fundraising prep, and scale-up support for high-growth ventures.'
    );
  }, []);

  return (
    <div className="service-page">
      <div className="service-hero">
        <div className="container">
          <h1>Start-up Growth Consultancy – From Idea to Execution</h1>
          <p className="subtitle">HRCONSOL provides end-to-end consulting for startups and scale-ups, helping businesses innovate, grow, and expand globally with strategic HR and business solutions.</p>
        </div>
      </div>

      <div className="container">
        <div className="service-content">
          <div className="service-section">
            <p>Building a startup is challenging—from securing funding to hiring the right team, scaling operations, and building a sustainable business model. HRCONSOL's <strong>Startup Consultancy Services</strong> guide entrepreneurs and businesses from idea stage to execution, with mentorship, funding strategies, and HR solutions tailored for high-growth ventures.</p>
            <p>We've partnered with 100+ startups across Pakistan, MENA, and beyond—helping them navigate challenges, attract talent, and achieve exponential growth.</p>
          </div>

          <div className="service-section">
            <h2>Scope of Services</h2>
            <p>Our Startup Consultancy Solutions Include:</p>
            <div className="process-steps">
              <div className="step">
                <h3><i className="fas fa-lightbulb"></i> Business Planning & Strategy</h3>
                <p>Business model development, market research, and go-to-market strategies.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-users"></i> Founding Team Building</h3>
                <p>Identifying and recruiting co-founders, CTOs, and key leadership roles.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-money-bill-wave"></i> Funding & Investment Support</h3>
                <p>Pitch deck preparation, investor connections, and funding strategy.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-chart-line"></i> Growth & Scaling Strategies</h3>
                <p>Expansion planning, product-market fit, and operational scaling.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-sitemap"></i> Organizational Design</h3>
                <p>Building org structures, defining roles, and creating scalable teams.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-handshake"></i> Partnership & Networking</h3>
                <p>Connecting with accelerators, incubators, mentors, and industry experts.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-globe"></i> International Expansion</h3>
                <p>Market entry strategies, global hiring, and cross-border operations.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-bullhorn"></i> Employer Branding</h3>
                <p>Building company culture, attracting talent, and creating strong employer brands.</p>
              </div>
            </div>
          </div>

          <div className="service-section">
            <h2>Startup Sectors We Serve</h2>
            <ul className="benefits-list">
              <li><i className="fas fa-coins"></i> Fintech & Banking</li>
              <li><i className="fas fa-heartbeat"></i> Healthtech & Telemedicine</li>
              <li><i className="fas fa-graduation-cap"></i> Edtech & E-Learning</li>
              <li><i className="fas fa-utensils"></i> Foodtech & Delivery</li>
              <li><i className="fas fa-shopping-cart"></i> E-Commerce & Retail Tech</li>
              <li><i className="fas fa-truck"></i> Logistics & Mobility</li>
              <li><i className="fas fa-leaf"></i> Green Energy & Sustainability</li>
              <li><i className="fas fa-gamepad"></i> Gaming & Entertainment</li>
              <li><i className="fas fa-laptop-code"></i> SaaS & Enterprise Software</li>
              <li><i className="fas fa-brain"></i> AI, ML & Deep Tech</li>
            </ul>
          </div>

          <div className="service-section">
            <h2>Why Choose HRCONSOL for Startup Consultancy?</h2>
            <p className="track-record-intro"><strong>Proven Startup Success Stories:</strong></p>
            <ul className="benefits-list">
              <li>
                <span className="benefit-heading">100+ Startups Supported</span>
                <span className="benefit-subheading">from seed stage to Series A and beyond</span>
              </li>
              <li>
                <span className="benefit-heading">$50M+ in Funding Raised</span>
                <span className="benefit-subheading">by startups we've consulted and supported</span>
              </li>
              <li>
                <span className="benefit-heading">75% Success Rate</span>
                <span className="benefit-subheading">of startups achieving product-market fit within 18 months</span>
              </li>
              <li>
                <span className="benefit-heading">Global Network</span>
                <span className="benefit-subheading">connections to VCs, accelerators, and industry leaders</span>
              </li>
              <li>
                <span className="benefit-heading">End-to-End Support</span>
                <span className="benefit-subheading">from ideation to scale-up and exit strategies</span>
              </li>
              <li>
                <span className="benefit-heading">HR-First Approach</span>
                <span className="benefit-subheading">building strong teams as the foundation for growth</span>
              </li>
            </ul>
          </div>

          <div className="service-section">
            <h2>Our Consultancy Process</h2>
            <div className="process-steps">
              <div className="step">
                <h3>Step 1: Discovery & Assessment</h3>
                <p>Understanding your vision, challenges, and current stage.</p>
              </div>
              <div className="step">
                <h3>Step 2: Strategic Planning</h3>
                <p>Creating roadmaps for growth, hiring, and operations.</p>
              </div>
              <div className="step">
                <h3>Step 3: Implementation</h3>
                <p>Executing strategies with hands-on support and guidance.</p>
              </div>
              <div className="step">
                <h3>Step 4: Team Building</h3>
                <p>Recruiting founders, developers, and key hires.</p>
              </div>
              <div className="step">
                <h3>Step 5: Growth & Optimization</h3>
                <p>Scaling operations, refining processes, and achieving milestones.</p>
              </div>
            </div>
          </div>

          <div className="service-section">
            <h2>Packages Details</h2>
            
            <div className="process-steps">
              <div className="step package-card">
                <h3>1. IDEATION</h3>
                <p>Pre-Seed Startups (Idea to MVP)</p>
                <ul className="package-list">
                  <li>Fee: PKR 150,000 (one-time)</li>
                  <li>Business model validation</li>
                  <li>Pitch deck creation</li>
                  <li>Founding team recruitment (up to 3 roles)</li>
                  <li>3 mentorship sessions</li>
                </ul>
                <p className="package-note">Perfect for first-time founders launching their venture.</p>
                <button onClick={() => navigate('/#contact')} className="package-button">Choose Plan</button>
              </div>

              <div className="step package-card">
                <h3>2. GROWTH</h3>
                <p>Seed to Series A (MVP to Scale)</p>
                <ul className="package-list">
                  <li>Fee: PKR 400,000 (6-month engagement)</li>
                  <li>Growth strategy & market expansion</li>
                  <li>Investor pitch preparation</li>
                  <li>Team building (10+ key hires)</li>
                  <li>Monthly strategy sessions</li>
                  <li>Investor introductions</li>
                </ul>
                <p className="package-note">Ideal for startups ready to scale and raise capital.</p>
                <button onClick={() => navigate('/#contact')} className="package-button">Choose Plan</button>
              </div>

              <div className="step package-card">
                <h3>3. SCALE-UP</h3>
                <p>Series A+ (Expansion & Optimization)</p>
                <ul className="package-list">
                  <li>Fee: Custom (retainer basis)</li>
                  <li>Full-scale consultancy & advisory</li>
                  <li>International expansion support</li>
                  <li>Executive recruitment</li>
                  <li>Organizational restructuring</li>
                  <li>Exit strategy planning</li>
                </ul>
                <button onClick={() => navigate('/#contact')} className="package-button">Choose Plan</button>
              </div>
            </div>
          </div>

          <div className="service-cta">
            <h2>Ready to Scale Your Startup?</h2>
            <p>Let HRCONSOL guide you from idea to execution and beyond.</p>
            <button onClick={() => navigate('/#contact')} className="cta-button">Contact Us</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartupConsultancy;

